"use client";

import { useBreakpointValue } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { IntersectionOptions, useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";
import { ProductPage } from "src/components/ProductOverview/ProductOverview";
import { SiteSettings } from "./cms/cms";
import { createContext } from "./react";
import { PortalType, TargetGroup, OnboardingType } from "./ssr/utils";

export const useLazyToggle = (
	options: IntersectionOptions,
): [(node?: Element | null) => void, boolean] => {
	const [lazyLoaded, set] = useState(false);
	const { ref, inView } = useInView(options);

	useEffect(() => {
		if (!lazyLoaded && inView) {
			set(true);
		}
	}, [inView, lazyLoaded]);

	return [ref, lazyLoaded];
};

export const useCurrencyFormatter = () => {
	const intl = useIntl();

	return useCallback(
		(value: number, currency = "EUR") =>
			intl.formatNumber(value, {
				style: "currency",
				currency,
			}),
		[intl],
	);
};

export type CurrencyFormatter = ReturnType<typeof useCurrencyFormatter>;

export const [SiteSettingsContext, useSiteSettings] =
	createContext<SiteSettings>("SiteSettingsContext");

export const [TargetGroupContext, useTargetGroup] =
	createContext<TargetGroup>("TargetGroupContext");

// Reveal the portal a user is currently in, since checking their role
//  is not enough when a broker should be able to visit the customer portal.
export const [PortalTypeContext, usePortalType] =
	createContext<PortalType>("PortalTypeContext");

export const [OnboardingSlidesContext, useOnboardingSlides] =
	createContext<OnboardingType>("OnboardingSlidesContext");

export const [PreviewContext, usePreview] =
	createContext<boolean>("PreviewContext");

export type Product = Pick<
	ProductPage,
	"slug" | "name" | "contact" | "productCategory" | "currentVersion"
>;
export const [ProductContext, useProduct] = createContext<Product | null>(
	"ProductContext",
);

export const usePagination = ({
	itemsPerPage,
	itemsTotal,
	breakpoint,
	overwriteMobileRule,
	cutoffThreshold = 0,
}: {
	itemsTotal: number;
	breakpoint: string;
	itemsPerPage?: number;
	overwriteMobileRule?: boolean;
	cutoffThreshold?: number;
}) => {
	const [page, setPage] = useState(1);

	const isMobile = useBreakpointValue(
		{ base: true, [breakpoint]: false },
		{ fallback: breakpoint, ssr: true },
	);

	const limit = isMobile
		? itemsPerPage
		: overwriteMobileRule
			? itemsPerPage
			: undefined;

	// e. g. 1 page * 3 itemsPerPage + 2 cutoffThreshold > 5 itemsTotal = false, use Pagination as usual
	// e. g. 1 page * 3 itemsPerPage + 2 cutoffThreshold > 4 itemsTotal = true, so append the remaining items to the last page
	const useCutoffThreshold = itemsPerPage
		? page * itemsPerPage + cutoffThreshold > itemsTotal
		: false;

	const maxPage =
		useCutoffThreshold && limit
			? Math.ceil((itemsTotal - cutoffThreshold) / limit)
			: limit
				? Math.ceil(itemsTotal / limit)
				: 1;

	const sliceItems =
		useCutoffThreshold && page >= maxPage
			? itemsTotal
			: limit
				? Math.min(itemsTotal, page * limit)
				: itemsTotal;

	const onShowNext = () => setPage((i) => (i >= maxPage ? i : i + 1));
	const onShowLess = () => setPage(1);
	const remainingItems = itemsTotal - sliceItems;
	const hasMore = remainingItems > 0;

	return {
		hasMore,
		isMobile,
		sliceItems,
		onShowNext,
		onShowLess,
		remainingItems,
	};
};

// 🔬 RTL
